import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Confetti from 'react-confetti';
import poko_waist from '../assets/poko_handsInWaist.png';
import poko_bulb from '../assets/poko_bulb.png';
import poko_celebrate from '../assets/poko_celebrate.png';
import poko_wave from '../assets/poko_waveHand.png';
import './QuizGamePage.css';
import { saveGameRecord, getGameRecords, getBestScore } from '../components/GameRecords';

// Imports all sign images from the 'letter' folder and returns an object mapping filenames (without extension) to their image modules.
function importAllSigns() {
	const context = require.context('../assets/signs/letter', false, /\.(png|jpe?g|svg)$/);
	const signs = {};
	context.keys().forEach((key) => {
		const fileName = key.replace('./', '').replace(/\.(png|jpe?g|svg)$/, '');
		signs[fileName] = context(key);
	});
	return signs;
}

const allSigns = importAllSigns();

// Helper function to get random options for a question
const getRandomOptions = (correctLetter, numOptions = 3) => {
	const allLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
	const otherLetters = allLetters.filter(letter => letter !== correctLetter);
	const shuffledLetters = otherLetters.sort(() => Math.random() - 0.5);
	const wrongOptions = shuffledLetters.slice(0, numOptions - 1);
	const allOptions = [...wrongOptions, correctLetter];
	return allOptions.sort(() => Math.random() - 0.5).map(letter => ({
		image: allSigns[letter],
		value: letter
	}));
};

// Generate a list of 26 multiple-choice questions for letters A–Z,
// where each question asks the user to identify the correct sign for a given letter.
// Each question includes randomly generated options and the correct answer.
const questions = Array.from({ length: 26 }, (_, i) => {
	const letter = String.fromCharCode(65 + i); // Convert 0–25 to 'A'–'Z'
	return {
		question: `Which sign is for the letter ${letter}?`,
		options: getRandomOptions(letter),
		correctAnswer: letter
	};
});

const QuizGamePage = () => {
	const navigate = useNavigate();
	const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
	const [score, setScore] = useState(0);
	const [showResults, setShowResults] = useState(false);
	const [selectedAnswer, setSelectedAnswer] = useState(null);
	const [answeredQuestions, setAnsweredQuestions] = useState([]);
	const [isStarted, setIsStarted] = useState(false);
	const [learntSigns, setLearntSigns] = useState([]);
	const [selectedOption, setSelectedOption] = useState('all'); // 'all' or 'learnt'
	const [filteredQuestions, setFilteredQuestions] = useState(questions);
	const [gameRecords, setGameRecords] = useState([]);
	const [bestScore, setBestScore] = useState(null);
	const [showPreviousRecords, setShowPreviousRecords] = useState(false);
	const [showConfetti, setShowConfetti] = useState(false);
	const [windowSize, setWindowSize] = useState({
		width: window.innerWidth,
		height: window.innerHeight
	});

	// Add useEffect to load learnt signs from localStorage
	useEffect(() => {
		const stored = localStorage.getItem('learntSigns');
		if (stored) {
			try {
				const parsed = JSON.parse(stored);
				setLearntSigns(parsed.letter || []);
			} catch (err) {
				console.error('Failed to parse learnt signs from localStorage', err);
			}
		}
	}, []);

	useEffect(() => {
		// Load game records and best score when component mounts
		const records = getGameRecords('quiz');
		setGameRecords(records);
		setBestScore(getBestScore('quiz'));
	}, []);

	// Add window resize handler
	useEffect(() => {
		const handleResize = () => {
			setWindowSize({
				width: window.innerWidth,
				height: window.innerHeight
			});
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const handleStartGame = () => {
		// Filter questions based on selected option
		let questionSet;
		if (selectedOption === 'learnt') {
			if (learntSigns.length === 0) {
				// If no learnt signs, use 10 random questions from all questions
				questionSet = [...questions].sort(() => Math.random() - 0.5).slice(0, 10);
			} else {
				// Convert learnt signs to uppercase to match question format
				const upperLearntSigns = learntSigns.map(sign => sign.toUpperCase());
				const learntQuestions = questions.filter(q => upperLearntSigns.includes(q.correctAnswer));

				// If less than 10 learnt signs, use all learnt questions
				// If more than 10, randomly select 10
				questionSet = learntQuestions.length <= 10
					? learntQuestions
					: [...learntQuestions].sort(() => Math.random() - 0.5).slice(0, 10);
			}
		} else {
			// For "all" option, randomly select 10 questions
			questionSet = [...questions].sort(() => Math.random() - 0.5).slice(0, 10);
		}

		// Ensure we have at least one question
		if (questionSet.length === 0) {
			questionSet = [...questions].sort(() => Math.random() - 0.5).slice(0, 10);
		}

		// Update state with filtered and shuffled questions
		setFilteredQuestions(questionSet);
		setCurrentQuestionIndex(0);
		setScore(0);
		setShowResults(false);
		setSelectedAnswer(null);
		setAnsweredQuestions([]);
		setIsStarted(true);
	};

	const handleAnswer = (answer) => {
		const isCorrect = answer === filteredQuestions[currentQuestionIndex].correctAnswer;
		
		if (isCorrect) {
			setShowConfetti(true); // Show confetti immediately for correct answer
			setScore(score + 1);
		}

		setSelectedAnswer(answer);
		setAnsweredQuestions([...answeredQuestions, {
			questionIndex: currentQuestionIndex,
			isCorrect
		}]);

		// Check if this was the last question
		if (currentQuestionIndex === filteredQuestions.length - 1) {
			// Game is over, save the record with the final score
			const saved = saveGameRecord('quiz', isCorrect ? score + 1 : score, filteredQuestions.length);
			if (saved) {
				// Update records display
				const records = getGameRecords('quiz');
				setGameRecords(records);
				setBestScore(getBestScore('quiz'));
			}
		}
	};

	// Advances to the next question or shows results if it's the last question
	const handleNext = () => {
		setShowConfetti(false); // Hide confetti when moving to next question
		if (currentQuestionIndex < filteredQuestions.length - 1) {
			setCurrentQuestionIndex(currentQuestionIndex + 1);
			setSelectedAnswer(null);
		} else {
			setShowResults(true);
		}
	};

	// Determines the user's badge level based on their quiz score percentage
	const getBadgeLevel = () => {
		const percentage = (score / filteredQuestions.length) * 100;
		if (percentage >= 80) return "🏆 🏆 🏆 Master Signer";
		if (percentage >= 50) return "🏆 🏆 Advanced Signer";
		if (percentage >= 30) return "🏆 Beginner Signer";
		return "🏅 New Signer";
	};

	if (!isStarted) {
		return (
			<div className="quiz-game-page page">
				<div className="white-wrapper container">
					<div className="game-page-title">
						<button className="game-back-btn" onClick={() => navigate('/games')}>⬅︎ Back to Games</button>
						<h1>✏️ Fun Quiz</h1>
					</div>

					<div className="speech-section">
						<img src={poko_waist} alt="Buddy" className="poko" />
						<div className="speech-bubble-right">
							<div className="game-instruction">
								It's fun quiz time! 🧠<br />
								Choose your sign set and start the game!
							</div>

							<div className="game-sign-option">
								<p>Choose your sign set:</p>
								<div className="radio-group">
									<label>
										<input
											type="radio"
											name="signSet"
											value="all"
											checked={selectedOption === 'all'}
											onChange={() => setSelectedOption('all')}
										/>
										All signs
										<div className="option-description">
											🌀 Play with all the signs, even the ones you haven't learned yet!
										</div>
									</label>

									<label className={learntSigns.length < 10 ? 'disabled' : ''}>
										<input
											type="radio"
											name="signSet"
											value="learnt"
											disabled={learntSigns.length < 10}
											checked={selectedOption === 'learnt'}
											onChange={() => setSelectedOption('learnt')}
										/>
										Learnt signs
										{learntSigns.length < 10 ? (
											<div className="option-description disabled">
												🧠 You've learnt <strong>{learntSigns.length} letter signs</strong> so far! <br />
												📖 Keep learning — unlock this mode after learning at least <strong>10 letter signs</strong>.<br />
												👉 <button onClick={() => navigate('/learn')}>Go to Learn</button>
											</div>
										) : (
											<div className="option-description">
												✅ You've learnt <strong>{learntSigns.length} signs</strong>! <br />
												🥳 Pick this mode to practice signs you already know. <br />
												👉 <button onClick={() => navigate('/learn')}>Want to learn more?</button>
											</div>
										)}
									</label>
								</div>
							</div>

							<button className="game-start-button" onClick={handleStartGame}>
								Start!
							</button>
						</div>
					</div>
				</div>
			</div>
		);
	}

	if (showResults) {
		return (
			<div className="quiz-game-page page">
				{showConfetti && (
					<Confetti
						width={windowSize.width}
						height={windowSize.height}
						numberOfPieces={500}
						recycle={true}
						gravity={0.3}
					/>
				)}
				<div className="white-wrapper container">
					<div className="game-page-title">
						<button className="game-back-btn" onClick={() => navigate('/games')}>⬅︎ Back to Games</button>
						<h1>Quiz Complete!</h1>
					</div>

					<div className="quiz-results">
						<div className="score-display">
							<h3>You earned the badge:</h3>
							<div className="badge-display">
								{getBadgeLevel()}
							</div>
							<h3>Your Score: {(score / filteredQuestions.length) * 100}%</h3>
						</div>

						<div className="records-container">
							{bestScore && (
								<div className="best-score-section">
									<div className="score-card">
										Best Score: {bestScore.percentage}%
									</div>
								</div>
							)}

							<div className="previous-records-section">
								<button
									className="records-toggle"
									onClick={() => setShowPreviousRecords(!showPreviousRecords)}
									aria-expanded={showPreviousRecords}
								>
									<span>Previous Records</span>
									<span className="toggle-icon">▼</span>
								</button>

								{showPreviousRecords && (
									<div className="records-list">
										{gameRecords.length > 0 ? (
											gameRecords.map((record, index) => (
												<div key={index} className="record-card">
													<div className="record-score">
														<span className="record-percentage">{record.percentage}%</span>
														<span className="record-fraction">({record.score}/{record.totalQuestions})</span>
													</div>
													<div className="record-date">
														{new Date(record.date).toLocaleDateString()}
													</div>
												</div>
											))
										) : (
											<p className="no-records">No records yet. Play more games to see your progress!</p>
										)}
									</div>
								)}
							</div>
						</div>

						<div className="review-section">
							<h3>💡 Let's Review These Signs</h3>
							<div className="review-signs-grid">
								{filteredQuestions.map((question, index) => (
									<div key={index} className="review-sign-card">
										<div className="review-sign-letter">{question.correctAnswer}</div>
										<img
											src={question.options.find(opt => opt.value === question.correctAnswer).image}
											alt={`Letter ${question.correctAnswer}`}
											className="review-sign-image"
										/>
										<div className={`review-sign-status ${answeredQuestions[index]?.isCorrect ? 'correct' : 'incorrect'}`}>
											{answeredQuestions[index]?.isCorrect ? '✓' : '✗'}
										</div>
									</div>
								))}
							</div>
						</div>
						<button onClick={() => navigate('/learn')} className="game-btn revisit-btn">🧠 Revisit in Learn Page</button>
						<div className='row-wrapper'>
							<button onClick={handleStartGame} className="game-btn">🔁 Play Again</button>
							<button onClick={() => navigate('/games')} className="game-btn">🎮 Try Another Game</button>
						</div>
					</div>
				</div>
			</div>
		);
	}

	const currentQuestion = filteredQuestions[currentQuestionIndex];

	return (
		<div className="quiz-game-page page">
			{showConfetti && (
				<Confetti
					width={windowSize.width}
					height={windowSize.height}
					numberOfPieces={200}
					recycle={false}
					gravity={0.3}
				/>
			)}
			<div className="white-wrapper container">
				<div className="game-page-title">
					<button className="game-back-btn" onClick={() => navigate('/games')}>⬅︎ Back to Games</button>
					<h2>✏️ Fun Quiz</h2>
				</div>

				<div className="quiz-main">
					<div className="quiz-content-row">
						<img
							src={selectedAnswer ?
								(selectedAnswer === currentQuestion.correctAnswer ? poko_celebrate : poko_wave)
								: poko_bulb
							}
							alt="Buddy"
							className="poko"
						/>
						<div className="speech-bubble-right">
							{!selectedAnswer ? (
								<div className="question-display">
									<h3 className="question-text">{currentQuestion.question}</h3>
									<div className="options-grid">
										{currentQuestion.options.map((option, index) => (
											<div
												key={index}
												className={`option-card ${selectedAnswer === option.value ? 'selected' : ''}`}
												onClick={() => !selectedAnswer && handleAnswer(option.value)}
											>
												<img
													src={option.image}
													alt={`Option ${option.value}`}
													className="option-image"
												/>
											</div>
										))}
									</div>
								</div>
							) : (
								<div className="feedback-display">
									<h3 className="feedback-text">
										{selectedAnswer === currentQuestion.correctAnswer ? (
											"🙌 High five! That's correct!"
										) : (
											"💪 Good try! You'll get the next one!"
										)}
									</h3>
									<div className="correct-answer-card-wrapper">
										<div className="correct-answer-card">
											<span className="letter">{currentQuestion.correctAnswer}</span>
											<img
												src={currentQuestion.options.find(opt => opt.value === currentQuestion.correctAnswer).image}
												alt={`Letter ${currentQuestion.correctAnswer}`}
												className="small-sign-image"
											/>
										</div>
									</div>
								</div>
							)}
						</div>
					</div>

					<div className="quiz-footer">
						<div className="progress-dots">
							{Array.from({ length: filteredQuestions.length }, (_, i) => i + 1).map((num) => (
								<div
									key={num}
									className={`progress-dot ${num === currentQuestionIndex + 1 ? 'active' :
											answeredQuestions.find(q => q.questionIndex === num - 1)?.isCorrect ? 'completed' : ''
										}`}
								>
									Q{num}
								</div>
							))}
						</div>

						{selectedAnswer && (
							<button className="game-btn" onClick={handleNext}>
								{currentQuestionIndex < filteredQuestions.length - 1 ? "Next Question" : "See Results"}
							</button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

export default QuizGamePage;